import { useEffect, useRef, useState } from 'react';

import { V3LiveDeltaProjection } from '@apps/web/features/phoebe_chat_v3/model/live_delta_projection';
import { type V3MessageEntry } from '@apps/web/features/phoebe_chat_v3/model/transcript';
import { getApiClient } from '@apps/web/lib/api_client';
import { type Mode } from '@libraries/typescript/api_client';

const LIVE_DELTA_PATH =
    '/organizations/{organization_id}/phoebe_agent_runs/{phoebe_agent_run_id}/events/live-deltas/stream';

type PublishedStream = {
    readonly organizationId: string | null;
    readonly runId: string | null;
    readonly mode: Mode;
    readonly isActive: boolean;
};

type StreamCursor = {
    readonly organizationId: string | null;
    readonly runId: string | null;
    readonly mode: Mode;
    readonly value: string;
};

export function useV3LiveDeltas({
    organizationId,
    runId,
    mode,
    enabled,
}: {
    readonly organizationId: string | null;
    readonly runId: string | null;
    readonly mode: Mode;
    readonly enabled: boolean;
}): {
    readonly entries: ReadonlyArray<V3MessageEntry>;
    readonly reasoningTitle: string | null;
    readonly errorCount: number;
} {
    const [entries, setEntries] = useState<ReadonlyArray<V3MessageEntry>>([]);
    const [reasoningTitle, setReasoningTitle] = useState<string | null>(null);
    const [errorCount, setErrorCount] = useState(0);
    const [publishedStream, setPublishedStream] = useState<PublishedStream>({
        organizationId,
        runId,
        mode,
        isActive: false,
    });
    const cursorRef = useRef<StreamCursor>({
        organizationId,
        runId,
        mode,
        value: '0-0',
    });

    useEffect(() => {
        setEntries([]);
        setReasoningTitle(null);
        setErrorCount(0);
        setPublishedStream({ organizationId, runId, mode, isActive: false });
        cursorRef.current = {
            organizationId,
            runId,
            mode,
            value: '0-0',
        };
    }, [mode, organizationId, runId]);

    useEffect(() => {
        if (!enabled || organizationId === null || runId === null) {
            setPublishedStream({
                organizationId,
                runId,
                mode,
                isActive: false,
            });
            return;
        }
        setEntries([]);
        setReasoningTitle(null);
        setErrorCount(0);
        setPublishedStream({ organizationId, runId, mode, isActive: true });
        const activeOrganizationId = organizationId;
        const activeRunId = runId;
        const abortController = new AbortController();
        const { signal } = abortController;
        const previousCursor = cursorRef.current;
        // Redis keeps recent deltas for the whole run. Resume after the last
        // turn so its reasoning is not replayed as the next turn begins.
        const initialCursor =
            previousCursor.organizationId === activeOrganizationId &&
            previousCursor.runId === activeRunId &&
            previousCursor.mode === mode
                ? previousCursor.value
                : '0-0';
        const projection = new V3LiveDeltaProjection(
            activeRunId,
            initialCursor,
        );

        function applyLine(line: string): boolean {
            const changed = projection.apply(JSON.parse(line));
            cursorRef.current = {
                organizationId: activeOrganizationId,
                runId: activeRunId,
                mode,
                value: projection.getCursor(),
            };
            return changed;
        }

        async function readResponse(
            body: ReadableStream<Uint8Array<ArrayBufferLike>> | null,
        ) {
            if (!body) {
                throw new Error('Live text stream response body is missing');
            }
            const reader = body.getReader();
            const decoder = new TextDecoder();
            let bufferedText = '';
            try {
                while (!signal.aborted) {
                    const result = await reader.read();
                    if (signal.aborted || result.done) {
                        break;
                    }
                    bufferedText += decoder.decode(result.value, {
                        stream: true,
                    });
                    const lines = bufferedText.split('\n');
                    bufferedText = lines.pop() ?? '';
                    let changed = false;
                    for (const line of lines) {
                        if (line.trim()) {
                            try {
                                changed = applyLine(line) || changed;
                            } catch {
                                // A broken temporary line must not stop the
                                // durable transcript or trap reconnect in a
                                // retry loop.
                            }
                        }
                    }
                    if (changed) {
                        setEntries(projection.toEntries());
                        setReasoningTitle(projection.latestReasoningTitle());
                    }
                }
                bufferedText += decoder.decode();
                if (!signal.aborted && bufferedText.trim()) {
                    try {
                        if (applyLine(bufferedText)) {
                            setEntries(projection.toEntries());
                            setReasoningTitle(
                                projection.latestReasoningTitle(),
                            );
                        }
                    } catch {
                        // The saved transcript remains authoritative when the
                        // temporary connection ends with a malformed record.
                    }
                }
            } finally {
                reader.releaseLock();
            }
        }

        async function run() {
            while (!signal.aborted) {
                try {
                    const response = await getApiClient().raw<
                        typeof LIVE_DELTA_PATH,
                        'get'
                    >({
                        method: 'GET',
                        url: LIVE_DELTA_PATH,
                        pathParams: {
                            organization_id: activeOrganizationId,
                            phoebe_agent_run_id: activeRunId,
                        },
                        queryParams: {
                            after_id: projection.getCursor(),
                            wait_ms: 25_000,
                        },
                        headers: { Accept: 'application/x-ndjson' },
                        mode,
                        reason: 'v3-live-agent-text',
                        signal,
                    });
                    await readResponse(response.body);
                    await new Promise<void>((resolve) => {
                        window.setTimeout(resolve, 25);
                    });
                } catch (error) {
                    if (
                        signal.aborted ||
                        (error instanceof Error && error.name === 'AbortError')
                    ) {
                        return;
                    }
                    setErrorCount((current) => current + 1);
                    await new Promise<void>((resolve) => {
                        window.setTimeout(resolve, 500);
                    });
                }
            }
        }

        void run();
        return () => {
            abortController.abort();
        };
    }, [enabled, mode, organizationId, runId]);

    const publishedForCurrentSelection =
        publishedStream.organizationId === organizationId &&
        publishedStream.runId === runId &&
        publishedStream.mode === mode;
    // Keep the finished turn visible while its saved transcript catches up.
    // Once the same run starts another turn, hide that retained text before
    // React paints and the new stream effect clears it.
    const showPublishedStream =
        publishedForCurrentSelection && (!enabled || publishedStream.isActive);
    return {
        entries: showPublishedStream ? entries : [],
        reasoningTitle: showPublishedStream ? reasoningTitle : null,
        errorCount: showPublishedStream ? errorCount : 0,
    };
}
